import { I18n } from "@assets/strings";
import React, { useEffect } from "react";
import styled from "styled-components";
import { useMySharerooms } from "@domain/group/hooks";
import { ArticleDraft } from "@domain/lms/article/article";
import { CourseDraft } from "@domain/lms/course/courseDraft";
import { QuizDraft } from "@domain/lms/quiz/quizDraft";
import { gap } from "@ui/styles/utils";
import { PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Tertiary } from "@ui/components/containers/textBlocks";
import { RadioButton } from "@ui/components/inputs/radio";
import { Toggle } from "@ui/components/inputs/toggle";
import { Row, Stack } from "@ui/components/layout";

interface ContentShareroomEditorProps {
	draft: CourseDraft | QuizDraft | ArticleDraft;
	onToggleWorkspace: (workspaceId: string) => void;
	onTogglePrivacy: (toggled: boolean) => void;
}
export const ContentShareroomEditor: React.FC<ContentShareroomEditorProps> = ({
	draft,
	onToggleWorkspace,
	onTogglePrivacy,
}) => {
	const { result: rooms, loading } = useMySharerooms(); // TODO Handle pagination ?
	const usableRooms = rooms.filter((room) => room.userRole === "ADMINISTRATOR");
	useEffect(() => {
		if (!loading) {
			const allSpaces = usableRooms.flatMap((room) => room.workspaces ?? []);
			for (const workspaceId of draft.workspaceIds) {
				// Cleanup workspaceId in content that the user can not access
				if (!allSpaces.find((space) => space.id === workspaceId)) {
					onToggleWorkspace(workspaceId);
				}
			}
		}
	}, [loading]);

	const invalid = draft.visibility === "PRIVATE" && draft.workspaceIds.size < 1;

	if (!usableRooms.length) {
		return null;
	}
	return (
		<>
			<PrivacyContainer>
				<Row gap={10} justify="space-between" align="center">
					<PrimaryBold>{I18n.t("content.privacy.toggle")}</PrimaryBold>
					<Toggle toggled={draft.visibility === "PRIVATE"} onChange={onTogglePrivacy} />
				</Row>
				{draft.visibility === "PRIVATE" && (
					<PrivacyWarning invalid={invalid}>{I18n.t("content.privacy.warning")}</PrivacyWarning>
				)}
			</PrivacyContainer>
			<ShareroomsContainer invalid={invalid}>
				<PrimaryBold>{I18n.t("course.editor.details.shareroom")}</PrimaryBold>
				{usableRooms.map((room) => (
					<Stack gap={10} key={room.id}>
						<PrimaryMedium>{room.name}</PrimaryMedium>
						<Stack gap={6}>
							{room.workspaces?.map((workspace) => (
								<RadioButton
									checkbox
									key={workspace.id}
									id={workspace.id}
									value={workspace.id}
									label={workspace.isDefault ? I18n.t("workspace.name.default") : workspace.name}
									name="workspace"
									checked={draft.workspaceIds?.has(workspace.id) ?? false}
									onChange={(id) => {
										onToggleWorkspace(id);
									}}
								/>
							))}
						</Stack>
					</Stack>
				))}
			</ShareroomsContainer>
		</>
	);
};

const PrivacyContainer = styled(RoundedBlock)`
	padding: 20px;
	${gap(10)};
`;
const ShareroomsContainer = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => prop !== "invalid",
})<{ invalid: boolean }>`
	padding: 20px;
	${gap(20)};
	${({ invalid, theme }) => invalid && `border-color: ${theme.colors.red}`};
`;

const PrivacyWarning = styled(Tertiary).withConfig({
	shouldForwardProp: (prop) => prop !== "invalid",
})<{ invalid: boolean }>`
	${({ invalid, theme }) => invalid && `color: ${theme.colors.red}`};
`;
